import React, {useEffect, useMemo, useRef, useState} from "react";
import services from "../../services/projects";
import ProjectsListItem from "../ProjectsListItem/ProjectsListItem";
import ProjectsListGridItem from "../ProjectsListGridItem/ProjectsListGridItem";
import SelectFilter from "../SelectFilter/SelectFilter";
import Button from "../Button/Button";
import {SingleValue} from "react-select";
import {Project} from "../../types/Project";
import {Filters} from "../../types/Filters";

type Props = {
    context: string
    rootElement: HTMLElement
}

const getFilters = () => {
    return JSON.parse(sessionStorage.getItem('selectedFilters') || '{}')
}

const ProjectsList: React.FC<Props> = ({context, rootElement}: Props) => {
    const [projects, setProjects] = useState<Project[]>([])
    const [from, setFrom] = useState<number>(0)
    const [componentType] = useState<string>(rootElement.dataset.type === 'latest' ? 'latest' : 'full')
    const [listType, setListType] = useState(componentType === 'latest' ? 'list' : sessionStorage.getItem('tc-projects-list-type') || 'grid')
    const [hasMoreProjects, setHasMoreProjects] = useState<boolean>(false)
    const [selectedFilters, setSelectedFilters] = useState<Filters>(getFilters() ? getFilters() : {
        businessArea: null,
        service: null,
        technology: null
    })

    let businessAreaData = useRef<string[]>([])
    let serviceData = useRef<string[]>([])
    let technologyData = useRef<string[]>([])

    let selectedTag = useRef<string | null>(null)
    selectedTag.current = sessionStorage.getItem('selectedTag') ? sessionStorage.getItem('selectedTag') : null

    const url = useMemo(() => {
        return context === 'development'
            ? 'https://local.techniconcept.ch/fr/projets.json'
            : rootElement.dataset.url ? rootElement.dataset.url : window.location.href.split('#')[0] + '.json'
    }, [context, rootElement.dataset.url]);

    const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 991px)').matches
    const isMobile = window.matchMedia('(max-width: 767px)').matches

    let hideListHeader = componentType === 'latest'

    useEffect(() => {
        document.querySelector('.techniconcept-loader')?.classList.add('visible')
        document.getElementById('projects-wrapper')?.classList.add('hidden')

        const limit = sessionStorage.getItem('tc-projects-nb-projects') ? sessionStorage.getItem('tc-projects-nb-projects') : isTablet ? 4 : isMobile ? 4 : null

        services.fetchData(url, {
            projectsList: true,
            from: 0,
            limit: limit,
            selectedFilters: selectedFilters,
            selectedTag: selectedTag.current,
            type: componentType
        }).then((data: any) => {
            preloadImages(data.projectsImages)
                .then(() => {
                    setTimeout(() => {
                        setProjects(data.projects)
                        setFrom(data.projects.length)
                        setHasMoreProjects(data.hasMoreProjects)

                        businessAreaData.current = data.filters.businessAreas
                        serviceData.current = data.filters.services
                        technologyData.current = data.filters.technologies

                        if (data.selectedTag) {
                            for (const key in data.filters) {
                                data.filters[key].forEach((filterData: any) => {
                                    if (filterData.value === data.selectedTag.value) {
                                        switch (key) {
                                            case 'businessAreas':
                                                setSelectedFilters((prevFilters) => ({
                                                    ...prevFilters,
                                                    businessArea: filterData
                                                }))
                                                updateFilters('businessArea', filterData)
                                                break
                                            case 'services':
                                                setSelectedFilters((prevFilters) => ({
                                                    ...prevFilters,
                                                    service: filterData
                                                }))
                                                updateFilters('service', filterData)
                                                break
                                            case 'technologies':
                                                setSelectedFilters((prevFilters) => ({
                                                    ...prevFilters,
                                                    technology: filterData
                                                }))
                                                updateFilters('technology', filterData)
                                                break
                                        }
                                    }
                                })
                            }

                            sessionStorage.removeItem('selectedTag')
                        }

                        document.getElementById('projects-wrapper')?.classList.remove('hidden')
                        document.querySelector('.techniconcept-loader')?.classList.remove('visible')

                        if (componentType === 'full') {
                            const projectsLoaded = new CustomEvent('projects-loaded')
                            window.dispatchEvent(projectsLoaded)
                        }
                    }, 0)
                })
                .catch((error: any) => {
                    console.error('Error fetching projects:', error);
                });
        })
    }, [url, selectedFilters, componentType, isTablet, isMobile, listType])

    const preloadImages = (projectsImages: any) => {
        const imagePromises = projectsImages.map((images: { grid: string; gridWide: string }) => {
            return Promise.all([
                new Promise((resolve) => {
                    const img = new Image()
                    img.src = images.grid
                    img.onload = resolve
                    img.onerror = resolve
                }),
                new Promise((resolve) => {
                    const img = new Image()
                    img.src = images.gridWide
                    img.onload = resolve
                    img.onerror = resolve
                })
            ])
        })

        return Promise.all(imagePromises)
    }

    const changeListType = (type: string) => {
        if (listType !== type) {
            sessionStorage.setItem('tc-projects-list-type', type)
            document.getElementById('projects-wrapper')?.classList.add('hidden')
            setTimeout(() => {
                setListType(type)
                document.getElementById('projects-wrapper')?.classList.remove('hidden')
            }, 400)
        }
    }

    const updateNumberOfProjects = (from: number) => {
        sessionStorage.setItem('tc-projects-nb-projects', from.toString())
    }

    const loadMoreProjects = () => {
        if (componentType === 'full') {
            document.querySelector('.techniconcept-loader')?.classList.add('visible')
            services.fetchData(url, {
                projectsList: true,
                from: from,
                limit: isTablet ? 3 : 4,
                selectedFilters: selectedFilters
            }).then((data) => {
                setProjects((prevProjects) => [...prevProjects, ...data.projects])
                setFrom((prevFrom) => {
                    const newFrom = prevFrom + data.projects.length
                    updateNumberOfProjects(newFrom)

                    return newFrom
                })
                setHasMoreProjects(data.hasMoreProjects)

                const newLoadedProjects = new CustomEvent('new-loaded-projects')
                window.dispatchEvent(newLoadedProjects)
                document.querySelector('.techniconcept-loader')?.classList.remove('visible')
            })
        } else if (rootElement.dataset.moreProjectsUrl) {
            const goToProjects = new CustomEvent('go-to-projects', {detail: {url: rootElement.dataset.moreProjectsUrl}})
            window.dispatchEvent(goToProjects)
        }
    }

    const onBusinessAreaChange = (value: SingleValue<{ label: string, value: string }>) => {
        setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            businessArea: value ? value : null
        }))

        updateFilters('businessArea', value)
    }

    const onServiceChange = (value: SingleValue<{ label: string, value: string }>) => {
        setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            service: value ? value : null
        }))

        updateFilters('service', value)
    }

    const onTechnologyChange = (value: SingleValue<{ label: string, value: string }>) => {
        setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            technology: value ? value : null
        }))

        updateFilters('technology', value)
    }

    const updateFilters = (key: string, value: SingleValue<{ label: string, value: string }>) => {
        let currentFilters = JSON.parse(sessionStorage.getItem('selectedFilters') || '{}')
        currentFilters[key] = value
        sessionStorage.setItem('selectedFilters', JSON.stringify(currentFilters))
    }

    const clearFilters = () => {
        sessionStorage.removeItem('selectedFilters')
    }

    const onResetFilters = () => {
        setSelectedFilters({
            businessArea: null,
            service: null,
            technology: null
        })

        clearFilters()
    }

    const onProjectClick = () => {
        sessionStorage.setItem('tc-projects-scroll', window.scrollY.toString())
    }

    const changeMoreButtonCursor = (action: string) => {
        const cursor = document.getElementById('custom-cursor')
        if (cursor) {
            cursor.classList.forEach((className) => {
                if (className.endsWith('-cursor')) {
                    cursor.classList.remove(className)
                }
            })

            action === 'enter' ? document.body.classList.add('gray-cursor') : document.body.classList.remove('gray-cursor')
        }
    }

    return (
        <div className={'projects-list'}>
            {!hideListHeader &&
                <div className={'list-header'}>
                    <div className={'list-filters'}>
                        <SelectFilter type={'businessArea'}
                                      options={[businessAreaData.current]}
                                      value={selectedFilters.businessArea}
                                      placeholder={rootElement.dataset.businessAreaFilterPlaceholder ? rootElement.dataset.businessAreaFilterPlaceholder : `Domaine d'activité`}
                                      onChange={onBusinessAreaChange}
                                      hasSelectedValue={!!selectedFilters.businessArea}
                                      key={`businessArea-${selectedFilters.businessArea?.value || 'default'}`}/>

                        <SelectFilter type={'service'}
                                      options={[serviceData.current]}
                                      value={selectedFilters.service}
                                      placeholder={rootElement.dataset.serviceFilterPlaceholder ? rootElement.dataset.serviceFilterPlaceholder : `Service`}
                                      onChange={onServiceChange}
                                      hasSelectedValue={!!selectedFilters.service}/>

                        <SelectFilter type={'technology'}
                                      options={[technologyData.current]}
                                      value={selectedFilters.technology}
                                      placeholder={rootElement.dataset.technologyFilterPlaceholder ? rootElement.dataset.technologyFilterPlaceholder : `Technologie`}
                                      onChange={onTechnologyChange}
                                      hasSelectedValue={!!selectedFilters.technology}/>
                    </div>

                    <div className={'list-type-wrapper'}>
                        <Button onClick={onResetFilters} id={'reset-filters'}
                                className={selectedFilters.businessArea || selectedFilters.service || selectedFilters.technology ? 'visible' : 'hidden'}>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M27.9458 10.2262L22.7733 5.05372C22.518 4.79822 22.2147 4.59554 21.881 4.45726C21.5473 4.31898 21.1896 4.2478 20.8283 4.2478C20.4671 4.2478 20.1094 4.31898 19.7757 4.45726C19.4419 4.59554 19.1387 4.79822 18.8833 5.05372L4.05333 19.8837C3.53771 20.3994 3.24805 21.0988 3.24805 21.8281C3.24805 22.5574 3.53771 23.2568 4.05333 23.7725L7.81208 27.53C7.9526 27.6707 8.14324 27.7498 8.34208 27.75H26.9996C27.1985 27.75 27.3893 27.6709 27.5299 27.5303C27.6706 27.3896 27.7496 27.1989 27.7496 27C27.7496 26.8011 27.6706 26.6103 27.5299 26.4696C27.3893 26.329 27.1985 26.25 26.9996 26.25H15.8108L27.9458 14.115C28.4615 13.5993 28.7511 12.8999 28.7511 12.1706C28.7511 11.4413 28.4615 10.7419 27.9458 10.2262ZM13.6883 26.25H8.65208L5.11458 22.7125C4.99836 22.5964 4.90617 22.4585 4.84326 22.3068C4.78035 22.155 4.74798 21.9924 4.74798 21.8281C4.74798 21.6638 4.78035 21.5012 4.84326 21.3494C4.90617 21.1977 4.99836 21.0598 5.11458 20.9437L11.9996 14.06L18.9396 21L13.6883 26.25ZM26.8846 13.055L19.9996 19.9387L13.0596 13L19.9446 6.11622C20.179 5.88188 20.4969 5.75023 20.8283 5.75023C21.1598 5.75023 21.4777 5.88188 21.7121 6.11622L26.8846 11.2875C27.1189 11.5219 27.2506 11.8398 27.2506 12.1712C27.2506 12.5027 27.1189 12.8206 26.8846 13.055Z"/>
                            </svg>
                        </Button>

                        <Button
                            onClick={() => changeListType('grid')}
                            className={listType === 'grid' ? 'active' : ''}
                            id={'list-type-grid'}>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M13 5.25H7C6.53587 5.25 6.09075 5.43437 5.76256 5.76256C5.43437 6.09075 5.25 6.53587 5.25 7V13C5.25 13.2298 5.29526 13.4574 5.38321 13.6697C5.47116 13.882 5.60006 14.0749 5.76256 14.2374C5.92507 14.3999 6.11798 14.5288 6.3303 14.6168C6.54262 14.7047 6.77019 14.75 7 14.75H13C13.2298 14.75 13.4574 14.7047 13.6697 14.6168C13.882 14.5288 14.0749 14.3999 14.2374 14.2374C14.3999 14.0749 14.5288 13.882 14.6168 13.6697C14.7047 13.4574 14.75 13.2298 14.75 13V7C14.75 6.77019 14.7047 6.54262 14.6168 6.3303C14.5288 6.11798 14.3999 5.92507 14.2374 5.76256C14.0749 5.60006 13.882 5.47116 13.6697 5.38321C13.4574 5.29526 13.2298 5.25 13 5.25ZM13.25 13C13.25 13.0663 13.2237 13.1299 13.1768 13.1768C13.1299 13.2237 13.0663 13.25 13 13.25H7C6.9337 13.25 6.87011 13.2237 6.82322 13.1768C6.77634 13.1299 6.75 13.0663 6.75 13V7C6.75 6.9337 6.77634 6.87011 6.82322 6.82322C6.87011 6.77634 6.9337 6.75 7 6.75H13C13.0663 6.75 13.1299 6.77634 13.1768 6.82322C13.2237 6.87011 13.25 6.9337 13.25 7V13ZM25 5.25H19C18.5359 5.25 18.0908 5.43437 17.7626 5.76256C17.4344 6.09075 17.25 6.53587 17.25 7V13C17.25 13.2298 17.2953 13.4574 17.3832 13.6697C17.4712 13.882 17.6001 14.0749 17.7626 14.2374C17.9251 14.3999 18.118 14.5288 18.3303 14.6168C18.5426 14.7047 18.7702 14.75 19 14.75H25C25.2298 14.75 25.4574 14.7047 25.6697 14.6168C25.882 14.5288 26.0749 14.3999 26.2374 14.2374C26.3999 14.0749 26.5288 13.882 26.6168 13.6697C26.7047 13.4574 26.75 13.2298 26.75 13V7C26.75 6.53587 26.5656 6.09075 26.2374 5.76256C25.9092 5.43437 25.4641 5.25 25 5.25ZM25.25 13C25.25 13.0663 25.2237 13.1299 25.1768 13.1768C25.1299 13.2237 25.0663 13.25 25 13.25H19C18.9337 13.25 18.8701 13.2237 18.8232 13.1768C18.7763 13.1299 18.75 13.0663 18.75 13V7C18.75 6.9337 18.7763 6.87011 18.8232 6.82322C18.8701 6.77634 18.9337 6.75 19 6.75H25C25.0663 6.75 25.1299 6.77634 25.1768 6.82322C25.2237 6.87011 25.25 6.9337 25.25 7V13ZM13 17.25H7C6.53587 17.25 6.09075 17.4344 5.76256 17.7626C5.43437 18.0908 5.25 18.5359 5.25 19V25C5.25 25.4641 5.43437 25.9092 5.76256 26.2374C6.09075 26.5656 6.53587 26.75 7 26.75H13C13.2298 26.75 13.4574 26.7047 13.6697 26.6168C13.882 26.5288 14.0749 26.3999 14.2374 26.2374C14.3999 26.0749 14.5288 25.882 14.6168 25.6697C14.7047 25.4574 14.75 25.2298 14.75 25V19C14.75 18.7702 14.7047 18.5426 14.6168 18.3303C14.5288 18.118 14.3999 17.9251 14.2374 17.7626C14.0749 17.6001 13.882 17.4712 13.6697 17.3832C13.4574 17.2953 13.2298 17.25 13 17.25ZM13.25 25C13.25 25.0663 13.2237 25.1299 13.1768 25.1768C13.1299 25.2237 13.0663 25.25 13 25.25H7C6.9337 25.25 6.87011 25.2237 6.82322 25.1768C6.77634 25.1299 6.75 25.0663 6.75 25V19C6.75 18.9337 6.77634 18.8701 6.82322 18.8232C6.87011 18.7763 6.9337 18.75 7 18.75H13C13.0663 18.75 13.1299 18.7763 13.1768 18.8232C13.2237 18.8701 13.25 18.9337 13.25 19V25ZM25 17.25H19C18.5359 17.25 18.0908 17.4344 17.7626 17.7626C17.4344 18.0908 17.25 18.5359 17.25 19V25C17.25 25.4641 17.4344 25.9092 17.7626 26.2374C18.0908 26.5656 18.5359 26.75 19 26.75H25C25.4641 26.75 25.9092 26.5656 26.2374 26.2374C26.5656 25.9092 26.75 25.4641 26.75 25V19C26.75 18.5359 26.5656 18.0908 26.2374 17.7626C25.9092 17.4344 25.4641 17.25 25 17.25ZM25.25 25C25.25 25.0663 25.2237 25.1299 25.1768 25.1768C25.1299 25.2237 25.0663 25.25 25 25.25H19C18.9337 25.25 18.8701 25.2237 18.8232 25.1768C18.7763 25.1299 18.75 25.0663 18.75 25V19C18.75 18.9337 18.7763 18.8701 18.8232 18.8232C18.8701 18.7763 18.9337 18.75 19 18.75H25C25.0663 18.75 25.1299 18.7763 25.1768 18.8232C25.2237 18.8701 25.25 18.9337 25.25 19V25Z"/>
                            </svg>
                        </Button>

                        <Button
                            onClick={() => changeListType('list')}
                            className={listType === 'list' ? 'active' : ''}
                            id={'list-type-list'}>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M11.25 8C11.25 7.80109 11.329 7.61032 11.4697 7.46967C11.6103 7.32902 11.8011 7.25 12 7.25H27C27.1989 7.25 27.3897 7.32902 27.5303 7.46967C27.671 7.61032 27.75 7.80109 27.75 8C27.75 8.19891 27.671 8.38968 27.5303 8.53033C27.3897 8.67098 27.1989 8.75 27 8.75H12C11.8011 8.75 11.6103 8.67098 11.4697 8.53033C11.329 8.38968 11.25 8.19891 11.25 8ZM27 15.25H12C11.8011 15.25 11.6103 15.329 11.4697 15.4697C11.329 15.6103 11.25 15.8011 11.25 16C11.25 16.1989 11.329 16.3897 11.4697 16.5303C11.6103 16.671 11.8011 16.75 12 16.75H27C27.1989 16.75 27.3897 16.671 27.5303 16.5303C27.671 16.3897 27.75 16.1989 27.75 16C27.75 15.8011 27.671 15.6103 27.5303 15.4697C27.3897 15.329 27.1989 15.25 27 15.25ZM27 23.25H12C11.8011 23.25 11.6103 23.329 11.4697 23.4697C11.329 23.6103 11.25 23.8011 11.25 24C11.25 24.1989 11.329 24.3897 11.4697 24.5303C11.6103 24.671 11.8011 24.75 12 24.75H27C27.1989 24.75 27.3897 24.671 27.5303 24.5303C27.671 24.3897 27.75 24.1989 27.75 24C27.75 23.8011 27.671 23.6103 27.5303 23.4697C27.3897 23.329 27.1989 23.25 27 23.25ZM7 7.25H5C4.80109 7.25 4.61032 7.32902 4.46967 7.46967C4.32902 7.61032 4.25 7.80109 4.25 8C4.25 8.19891 4.32902 8.38968 4.46967 8.53033C4.61032 8.67098 4.80109 8.75 5 8.75H7C7.19891 8.75 7.38968 8.67098 7.53033 8.53033C7.67098 8.38968 7.75 8.19891 7.75 8C7.75 7.80109 7.67098 7.61032 7.53033 7.46967C7.38968 7.32902 7.19891 7.25 7 7.25ZM7 15.25H5C4.80109 15.25 4.61032 15.329 4.46967 15.4697C4.32902 15.6103 4.25 15.8011 4.25 16C4.25 16.1989 4.32902 16.3897 4.46967 16.5303C4.61032 16.671 4.80109 16.75 5 16.75H7C7.19891 16.75 7.38968 16.671 7.53033 16.5303C7.67098 16.3897 7.75 16.1989 7.75 16C7.75 15.8011 7.67098 15.6103 7.53033 15.4697C7.38968 15.329 7.19891 15.25 7 15.25ZM7 23.25H5C4.80109 23.25 4.61032 23.329 4.46967 23.4697C4.32902 23.6103 4.25 23.8011 4.25 24C4.25 24.1989 4.32902 24.3897 4.46967 24.5303C4.61032 24.671 4.80109 24.75 5 24.75H7C7.19891 24.75 7.38968 24.671 7.53033 24.5303C7.67098 24.3897 7.75 24.1989 7.75 24C7.75 23.8011 7.67098 23.6103 7.53033 23.4697C7.38968 23.329 7.19891 23.25 7 23.25Z"/>
                            </svg>
                        </Button>
                    </div>
                </div>
            }

            <div id={'projects-wrapper'} className={listType}>
                {projects.map((project, index) => (
                    listType === 'grid' ?
                        <ProjectsListGridItem
                            key={index}
                            index={index}
                            project={project}
                            isMobile={isMobile}
                            onProjectClick={onProjectClick}
                        />
                        :
                        <ProjectsListItem
                            key={index}
                            project={project}
                            onProjectClick={onProjectClick}
                        />
                ))}
            </div>

            {hasMoreProjects &&
                <div
                    onClick={loadMoreProjects}
                    onMouseEnter={() => changeMoreButtonCursor('enter')}
                    onMouseLeave={() => changeMoreButtonCursor('leave')}
                    id={'load-more-projects'}
                    data-cursor-type={'circle'}
                    data-cursor-color={'gray'}>
                    <div className={'btn btn-primary'}>{rootElement.dataset.loadMoreProjectsLabel}</div>
                </div>
            }

            <div className={'techniconcept-loader'}>
                <div className={'green-bar'}></div>
            </div>
        </div>
    )
}

export default ProjectsList
